/**
 * POST /impact/result — the real PIC (Philanthropy Impact Calculator)
 * allocator. This is the sole source for the Overview Panel's aggregate
 * stats and the Impact Projections numbers — there is no local fallback
 * computation. Callers must show an explicit "couldn't load" state on
 * failure, not a substitute number.
 *
 * Age mapping: our age-range UI ids (`under_5` / `5_to_9` / `10_to_14` /
 * `15_to_17`) are literally the API's ACS bins, so a selection is sent
 * straight through as `brackets` — see `config/dropdowns/ageRanges.ts`'s
 * `bracketsFromAgeIds`. No `start_age`/`fraction` compatibility shim needed.
 */
import { postJson } from '../../../services/apiClient';
import type { AgeBracket } from '../../../config/dropdowns';
import type { AreaMetrics, ImpactResult, OverviewMetrics, ProjectionPoint } from '../data/impactTypes';
import type { GeoLevel } from '../types';

interface ImpactResultRequestBody {
  level: GeoLevel;
  fips: string[];
  amount_dollars: number;
  brackets: AgeBracket[];
}

interface ImpactResultResponseBody {
  areas: AreaMetrics[];
  overview: OverviewMetrics;
  projection: ProjectionPoint[];
  reachChildren: number;
  perChild: number;
  totalWealthSavings: number;
}

export interface FetchImpactResultParams {
  level: GeoLevel;
  fips: string[];
  amountDollars: number;
  brackets: AgeBracket[];
}

export async function fetchImpactResult({
  level,
  fips,
  amountDollars,
  brackets,
}: FetchImpactResultParams): Promise<ImpactResult> {
  const body: ImpactResultRequestBody = {
    level,
    fips,
    amount_dollars: amountDollars,
    brackets,
  };
  const data = await postJson<ImpactResultResponseBody>('/impact/result', body);
  return {
    areas: data.areas,
    overview: data.overview,
    projection: data.projection,
    reachChildren: data.reachChildren,
    perChild: data.perChild,
    totalWealthSavings: data.totalWealthSavings,
  };
}
